import { useEffect, useState } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { Plus, PencilSquare } from "react-bootstrap-icons";
import Swal from 'sweetalert2';
import api from "../api/axios";

export default function TenantPage() {
  const [tenants, setTenants] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [search, setSearch] = useState("");
  const [errors, setErrors] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    name: "",
    nit: "",
    address: "",
    phone: "",
    email: "",
  });

  // ==============================
  // Fetch inicial
  // ==============================
  useEffect(() => {
    fetchTenants();
  }, []);

  const normalizeText = (text) => {
    return text
      ?.toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  };

  const fetchTenants = async () => {
    try {
      setLoadingTable(true);
      const res = await api.get("/tenants/list");
      setTenants(res.data.data);
      setFilteredTenants(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingTable(false);
    }
  };


  // ==============================
  // Filtrar búsqueda
  // ==============================
  useEffect(() => {
    const result = tenants.filter((t) =>
      normalizeText(t.name)?.includes(normalizeText(search)) ||
      normalizeText(t.nit)?.includes(normalizeText(search)) ||
      normalizeText(t.email)?.includes(normalizeText(search))
    );
    setFilteredTenants(result);
  }, [search, tenants]);


  // ==============================
  // Modal: abrir - cerrar
  // ==============================
  const handleShowNew = () => {
    setIsEditing(false);
    setErrors({});
    setFormData({
      id: null,
      name: "",
      nit: "",
      address: "",
      phone: "",
      email: "",
    });
    setShowModal(true);
  };

  const handleShowEdit = (tenant) => {
    setIsEditing(true);
    setErrors({});
    setFormData({
      id: tenant.id,
      name: tenant.name,
      nit: tenant.nit,
      address: tenant.address,
      phone: tenant.phone,
      email: tenant.email,
    });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setIsEditing(false);
    // fetchTenants(); // refrescar tabla
  };

  // ==============================
  // Guardar (crear o editar)
  // ==============================
  const handleSave = async () => {
    setLoadingSave(true);
    setErrors({}); // limpiar errores

    const payload = {
      ...formData,
      name: formData.name?.toUpperCase() ?? "",
      address: formData.address?.toUpperCase() ?? "",
    };

    try {
      if (isEditing) {
        await api.put(`/tenants/update/${formData.id}`, payload);
      } else {
        await api.post("/tenants/create", payload);
      }

      fetchTenants();
      handleClose();

    } catch (err) {
      console.error(err);

      if (err.response?.status === 422 && err.response.data.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrors({ general: ["Error desconocido al guardar"] });
        Swal.fire({
          title: 'Error',
          text: "Error desconocido al guardar",
          icon: 'error',
          timer: 1500,
          showConfirmButton: false
        });
      }

    } finally {
      setLoadingSave(false);
    }
  };


  // ==============================
  // Columnas tabla
  // ==============================
  const columns = [
    { name: "Nombre", selector: (row) => row.name, sortable: true },
    { name: "NIT", selector: (row) => row.nit, sortable: true },
    { name: "Dirección", selector: (row) => row.address, sortable: true },
    { name: "Teléfono", selector: (row) => row.phone, sortable: true },
    { name: "Email", selector: (row) => row.email, sortable: true },

    {
      name: "Opciones",
      cell: (row) => (
        <Button
          variant="outline-info"
          size="sm"
          onClick={() => handleShowEdit(row)}
        >
          <PencilSquare />
        </Button>
      ),
      ignoreRowClick: true,
    },
  ];

  return (
    <div className="mt-4">
      {/* TABLA */}
      <DataTable
        title="Empresas de Taxi"
        columns={columns}
        data={filteredTenants}
        pagination
        highlightOnHover
        responsive
        striped
        progressPending={loadingTable}
        progressComponent={
          <div className="text-center p-3">
            <div className="spinner-border text-primary"></div>
            <p className="mt-2">Cargando datos...</p>
          </div>
        }
        subHeader
        subHeaderComponent={
          <div className="d-flex justify-content-between w-100">
            <Button variant="outline-success" size="sm" onClick={handleShowNew}>
              Nuevo <Plus />
            </Button>

            <input
              type="text"
              className="form-control w-25"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        }
      />

      {/* MODAL */}
      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? "Editar Empresa" : "Nueva Empresa"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
                {errors.name?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>NIT</Form.Label>
              <Form.Control
                type="text"
                value={formData.nit}
                onChange={(e) =>
                  setFormData({ ...formData, nit: e.target.value })
                }
              />
                {errors.nit?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Dirección</Form.Label>
              <Form.Control
                type="text"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />
                {errors.address?.map((e, i) => (
                  <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
                ))}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Teléfono</Form.Label>
              <Form.Control
                type="text"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
              />
              {errors.phone?.map((e, i) => (
                <Form.Text key={i} className="text-danger d-block">{e}</Form.Text>
              ))}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="text"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>

          <Button variant="primary" onClick={handleSave} disabled={loadingSave}>
            {loadingSave ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Guardando...
              </>
            ) : (
              isEditing ? "Actualizar" : "Guardar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
